import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Upload, Download, Trash2, Pencil, ScanFace, IdCard, UserCog, UserPlus2, Users as UsersIcon, ShieldCheck, UserMinus, Eye, FileDown, TreePine, Calendar } from "lucide-react";
import { toast } from "sonner";
import { fileToBase64, designationCategory, errorMsg } from "@/lib/utils-app";
import { PasswordInput } from "@/components/PasswordInput";
import { Link } from "react-router-dom";

export default function EmployeesPage() {
  const [parks, setParks] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contractorId, setContractorId] = useState("");
  const [parkId, setParkId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingEmps, setLoadingEmps] = useState(false);

  const approvedCount = useMemo(() => employees.filter((e) => !e.is_reliever).length, [employees]);
  const relieverCount = useMemo(() => employees.filter((e) => e.is_reliever).length, [employees]);

  const [supState, setSupState] = useState({ supervisor: null, loading: false });

  const [empCategoryTab, setEmpCategoryTab] = useState("all");
  const [inchargeCandidates, setInchargeCandidates] = useState([]);
  const [assignedIncharge, setAssignedIncharge] = useState([]);
  const [loadingIncharge, setLoadingIncharge] = useState(false);

  const sweepersAndDriversOnly = useMemo(() => {
    return inchargeCandidates.filter((emp) => {
      const cat = designationCategory(emp.designation);
      return cat === "Sweeper" || cat === "Driver";
    });
  }, [inchargeCandidates]);

  const loadInchargeData = async (targetParkId) => {
    if (!targetParkId) {
      setInchargeCandidates([]);
      setAssignedIncharge([]);
      return;
    }
    setLoadingIncharge(true);
    try {
      const [candRes, assignRes] = await Promise.all([
        api.get(`/admin/parks/${targetParkId}/incharge-candidates`),
        api.get(`/admin/parks/${targetParkId}/incharge`),
      ]);
      setInchargeCandidates(candRes.data || []);
      setAssignedIncharge(assignRes.data || []);
    } catch (e) {
      console.error("Failed to load incharge data:", e);
    } finally {
      setLoadingIncharge(false);
    }
  };

  useEffect(() => {
    if (parkId) {
      loadInchargeData(parkId);
    }
  }, [parkId]);

  const fetchEmployees = async () => {
    setLoadingEmps(true);
    try {
      const params = {};
      if (contractorId) params.contractor_id = contractorId;
      if (parkId) params.park_id = parkId;
      const r = await api.get("/employees", { params });
      setEmployees(r.data);
    } catch (e) {
      console.error("Failed to load employees:", e);
    } finally {
      setLoadingEmps(false);
    }
  };

  const handleAssignIncharge = async (empId) => {
    if (!parkId) {
      toast.error("Please select a park first");
      return;
    }
    try {
      if (assignedIncharge.length > 0) {
        for (const prev of assignedIncharge) {
          if (prev.id !== empId) {
            await api.delete(`/admin/parks/${parkId}/incharge/${prev.id}`);
          }
        }
      }
      await api.post(`/admin/parks/${parkId}/incharge`, { employee_id: empId, park_id: parkId });
      toast.success("Employee assigned as In-Charge");
      await loadInchargeData(parkId);
      await fetchEmployees();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to assign In-Charge"));
    }
  };

  const handleUnassignIncharge = async (empId) => {
    if (!parkId) return;
    try {
      await api.delete(`/admin/parks/${parkId}/incharge/${empId}`);
      toast.success("In-Charge assignment removed");
      await loadInchargeData(parkId);
      await fetchEmployees();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to remove In-Charge"));
    }
  };

  const [empOpen, setEmpOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    employee_code: "", full_name: "", designation: "", phone: "", reference_photo_b64: "",
    park_id: "", contractor_id: "",
    id_card_type: "aadhar", id_card_other_label: "", id_card_number: "", id_card_attachment_b64: "",
    bank_account_number: "", bank_name: "", bank_branch: "", bank_ifsc: "", bank_passbook_attachment_b64: "",
  });
  const [saving, setSaving] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkExcel, setBulkExcel] = useState(null);
  const [bulkZip, setBulkZip] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [supDialogOpen, setSupDialogOpen] = useState(false);
  const [supMode, setSupMode] = useState("create"); // 'create' | 'assign'
  const [supForm, setSupForm] = useState({ username: "", full_name: "", password: "", phone: "", email: "" });
  const [allContractorSups, setAllContractorSups] = useState([]);
  const [assignSelectId, setAssignSelectId] = useState("");
  const [savingSup, setSavingSup] = useState(false);

  // ---------- bootstrap
  useEffect(() => {
    Promise.all([api.get("/parks"), api.get("/contractors"), api.get("/settings")]).then(([p, c, s]) => {
      setParks(p.data); setContractors(c.data);
      setCategories(s.data.worker_categories || []);
      if (c.data[0]) setContractorId(c.data[0].id);
    });
  }, []);

  // ---------- when contractor or park changes -> load employees + supervisor
  useEffect(() => {
    if (!contractorId) {
      // Without a contractor filter, still load all employees for admin overview.
      setLoadingEmps(true);
      const params = parkId ? { park_id: parkId } : {};
      api.get("/employees", { params }).then((r) => setEmployees(r.data)).finally(() => setLoadingEmps(false));
      setSupState({ supervisor: null, loading: false });
      return;
    }
    const params = { contractor_id: contractorId };
    if (parkId) params.park_id = parkId;
    setLoadingEmps(true);
    api.get("/employees", { params }).then((r) => setEmployees(r.data)).finally(() => setLoadingEmps(false));
    if (parkId) {
      setSupState({ supervisor: null, loading: true });
      api.get("/admin/contractor-supervisor", { params: { contractor_id: contractorId, park_id: parkId } })
        .then((r) => setSupState({ supervisor: r.data.supervisor, loading: false }))
        .catch(() => setSupState({ supervisor: null, loading: false }));
    } else {
      setSupState({ supervisor: null, loading: false });
    }
  }, [contractorId, parkId]);

  const filtered = employees.filter((e) => {
    const matchSearch = `${e.full_name} ${e.employee_code}`.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (empCategoryTab === "sweeper") return (e.designation || "").toLowerCase() === "sweeper";
    if (empCategoryTab === "driver") return (e.designation || "").toLowerCase() === "driver";
    if (empCategoryTab === "incharge") return e.is_incharge || (e.incharge_park_ids || []).length > 0;
    return true;
  });

  // ---------- employee CRUD
  const onPhoto = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, reference_photo_b64: b64 }));
  };
  const openView = async (e) => {
    setViewing(e);
    setViewOpen(true);
    try {
      const r = await api.get(`/employees/${e.id}/photo`);
      const photo = r.data.photo_b64 || "";
      if (photo) {
        const fullPhoto = photo.startsWith("data:") ? photo : `data:image/jpeg;base64,${photo}`;
        setViewing((v) => v && v.id === e.id ? { ...v, photo_b64: fullPhoto, has_photo: true } : v);
      }
    } catch {}
  };

  const openCreate = () => {
    if (!parkId || !contractorId) return toast.error("Pick a contractor and a park first");
    setEditing(null);
    setForm({
      employee_code: "", full_name: "", designation: categories[0] || "", phone: "",
      park_id: parkId, contractor_id: contractorId,
      reference_photo_b64: "", initial_photo_b64: "",
      id_card_type: "aadhar", id_card_other_label: "", id_card_number: "", id_card_attachment_b64: "",
      bank_account_number: "", bank_name: "", bank_branch: "", bank_ifsc: "", bank_passbook_attachment_b64: "",
    });
    setEmpOpen(true);
  };
  const openEdit = async (e) => {
    setEditing(e);
    let photo = "";
    try { const r = await api.get(`/employees/${e.id}/photo`); photo = r.data.photo_b64 || ""; } catch {}
    const formattedPhoto = photo ? (photo.startsWith("data:") ? photo : `data:image/jpeg;base64,${photo}`) : "";
    setForm({
      employee_code: e.employee_code, full_name: e.full_name, designation: e.designation || "",
      phone: e.phone || "",
      park_id: e.park_id || parkId || "",
      contractor_id: e.contractor_id || contractorId || "",
      reference_photo_b64: formattedPhoto,
      initial_photo_b64: formattedPhoto,
      id_card_type: e.id_card_type || "aadhar",
      id_card_other_label: e.id_card_other_label || "",
      id_card_number: e.id_card_number || "",
      id_card_attachment_b64: "", // not pre-loaded; admin can re-upload to replace
      bank_account_number: e.bank_account_number || "",
      bank_name: e.bank_name || "",
      bank_branch: e.bank_branch || "",
      bank_ifsc: e.bank_ifsc || "",
      bank_passbook_attachment_b64: "",
    });
    setEmpOpen(true);
  };
  const submitEmp = async () => {
    if (!form.employee_code || !form.full_name) return toast.error("Code and name required");
    setSaving(true);
    try {
      const targetParkId = form.park_id || editing?.park_id || parkId;
      const targetContractorId = form.contractor_id || editing?.contractor_id || contractorId;
      const payload = { ...form };
      if (targetParkId) payload.park_id = targetParkId;
      if (targetContractorId) payload.contractor_id = targetContractorId;

      // strip empty attachments so we don't overwrite stored ones with ""
      if (!payload.id_card_attachment_b64) delete payload.id_card_attachment_b64;
      if (!payload.bank_passbook_attachment_b64) delete payload.bank_passbook_attachment_b64;
      if (form.id_card_type !== "other") payload.id_card_other_label = "";
      if (editing) {
        const upd = {
          full_name: payload.full_name, designation: payload.designation, phone: payload.phone,
          id_card_type: payload.id_card_type, id_card_other_label: payload.id_card_other_label, id_card_number: payload.id_card_number,
          bank_account_number: payload.bank_account_number, bank_name: payload.bank_name,
          bank_branch: payload.bank_branch, bank_ifsc: payload.bank_ifsc,
        };
        if (targetParkId) upd.park_id = targetParkId;
        if (targetContractorId) upd.contractor_id = targetContractorId;
        if (form.reference_photo_b64 && form.reference_photo_b64 !== form.initial_photo_b64) {
          upd.reference_photo_b64 = form.reference_photo_b64;
        }
        if (payload.id_card_attachment_b64) upd.id_card_attachment_b64 = payload.id_card_attachment_b64;
        if (payload.bank_passbook_attachment_b64) upd.bank_passbook_attachment_b64 = payload.bank_passbook_attachment_b64;
        await api.put(`/employees/${editing.id}`, upd);
      } else {
        if (!targetParkId || !targetContractorId) return toast.error("Pick a contractor and a park first");
        await api.post("/employees", payload);
      }
      toast.success("Saved");
      setEmpOpen(false);
      const params = { contractor_id: contractorId || undefined, park_id: parkId || undefined };
      const r = await api.get("/employees", { params });
      setEmployees(r.data);
    } catch (e) { toast.error(errorMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Delete employee '${e.full_name}'?`)) return;
    try {
      await api.delete(`/employees/${e.id}`);
      toast.success("Deleted");
      setEmployees(employees.filter((x) => x.id !== e.id));
    } catch (err) { toast.error(errorMsg(err, "Delete failed")); }
  };

  // ---------- export to Excel
  const exportXlsx = async () => {
    try {
      const tk = tokenStore.get();
      const qs = new URLSearchParams();
      if (contractorId) qs.set("contractor_id", contractorId);
      if (parkId) qs.set("park_id", parkId);
      const res = await fetch(`${API_BASE}/employees/export.xlsx?${qs.toString()}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return toast.error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `employees_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  // ---------- bulk import
  const downloadTemplate = async () => {
    const tk = tokenStore.get();
    const res = await fetch(`${API_BASE}/employees/template`, { headers: { Authorization: `Bearer ${tk}` } });
    if (!res.ok) return toast.error("Template download failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "employees_template.xlsx"; a.click(); URL.revokeObjectURL(a.href);
  };
  const submitBulk = async () => {
    if (!bulkExcel) return toast.error("Excel file required");
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("excel", bulkExcel);
      if (bulkZip) fd.append("photos_zip", bulkZip);
      if (contractorId) fd.append("contractor_id", contractorId);
      if (parkId) fd.append("park_id", parkId);
      const { data } = await api.post("/employees/bulk_import", fd);
      setBulkResult(data);
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      const r = await api.get("/employees", { params: { contractor_id: contractorId || undefined, park_id: parkId || undefined } });
      setEmployees(r.data);
    } catch (e) { toast.error(errorMsg(e, "Bulk import failed")); }
    finally { setBulkBusy(false); }
  };

  // ---------- contractor supervisor assignment
  const openSupDialog = async (mode) => {
    if (!parkId) return toast.error("Pick a park first");
    setSupMode(mode);
    if (mode === "assign") {
      const { data } = await api.get("/users", { params: { role: "contractor_supervisor" } });
      // Each contractor supervisor is single-park, so only those with no park (or
      // already this park) are reasonable choices to "move" here.
      const eligible = (data || []).filter((u) =>
        u.contractor_id === contractorId && (!u.park_ids || u.park_ids.length === 0 || u.park_ids[0] === parkId)
      );
      setAllContractorSups(eligible);
      setAssignSelectId("");
    } else {
      setSupForm({ username: "", full_name: "", password: "", phone: "", email: "" });
    }
    setSupDialogOpen(true);
  };
  const submitSup = async () => {
    setSavingSup(true);
    try {
      if (supMode === "create") {
        if (!supForm.username || !supForm.full_name || !supForm.password) return toast.error("Username, name and password required");
        const cleaned = { ...supForm };
        ["email", "phone"].forEach((k) => {
          if (typeof cleaned[k] === "string" && cleaned[k].trim() === "") delete cleaned[k];
        });
        await api.post("/admin/contractor-supervisor/create-and-assign", {
          ...cleaned,
          contractor_id: contractorId,
          park_id: parkId,
        });
      } else {
        if (!assignSelectId) return toast.error("Pick a supervisor");
        await api.post("/admin/contractor-supervisor/assign", {
          user_id: assignSelectId,
          contractor_id: contractorId,
          park_id: parkId,
        });
      }
      toast.success("Supervisor assigned");
      setSupDialogOpen(false);
      const r = await api.get("/admin/contractor-supervisor", { params: { contractor_id: contractorId, park_id: parkId } });
      setSupState({ supervisor: r.data.supervisor, loading: false });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join("; ")
        : detail || "Failed";
      toast.error(msg);
    }
    finally { setSavingSup(false); }
  };
  const unassignSup = async () => {
    if (!supState.supervisor) return;
    if (!window.confirm(`Remove ${supState.supervisor.full_name} from this park?`)) return;
    try {
      await api.post("/admin/contractor-supervisor/unassign", {
        user_id: supState.supervisor.id,
        contractor_id: contractorId,
        park_id: parkId,
      });
      toast.success("Unassigned");
      setSupState({ supervisor: null, loading: false });
    } catch (e) { toast.error(errorMsg(e, "Failed")); }
  };

  const parkObj = parks.find((p) => p.id === parkId);
  const contractorObj = contractors.find((c) => c.id === contractorId);

  // -------- reliever toggle
  const toggleReliever = async (emp) => {
    const next = !emp.is_reliever;
    try {
      await api.patch(`/employees/${emp.id}/reliever`, { is_reliever: next });
      setEmployees((prev) => prev.map((x) => (x.id === emp.id ? { ...x, is_reliever: next } : x)));
      toast.success(next ? `Marked ${emp.full_name} as Reliever` : `Marked ${emp.full_name} as Approved`);
    } catch (err) {
      toast.error(errorMsg(err, "Failed to update"));
    }
  };

  // -------- bucket summary (approved vs reliever per category for selected park)
  const summary = React.useMemo(() => {
    if (!parkObj) return null;
    const cats = ["Sweeper", "Driver", "Supervisor"];
    const approved = {
      Sweeper: parkObj.approved_sweeper || 0,
      Driver: parkObj.approved_driver || 0,
      Supervisor: parkObj.approved_supervisor || 0,
    };
    const out = cats.map((c) => {
      // `designation` is free-text — bucket via the canonical categorizer so
      // contractor synonyms like "Mazdoor" / "Helper" / "Cleaner" still land
      // in the Sweeper bucket. Mirrors backend `_cat_of`.
      const inCat = employees.filter((e) => designationCategory(e.designation) === c);
      const approvedCount = inCat.filter((e) => !e.is_reliever).length;
      const relieverCount = inCat.filter((e) => e.is_reliever).length;
      return {
        cat: c,
        approved: approved[c],
        actual_approved: approvedCount,
        reliever: relieverCount,
        total: inCat.length,
        shortage: Math.max(0, approved[c] - approvedCount),
      };
    });
    return out;
  }, [parkObj, employees]);

  return (
    <AppShell title="Employees" subtitle="Pick a contractor, then a park, then manage employees and the assigned supervisor for that park." action={
      <div className="flex gap-2">
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild><Button variant="outline" data-testid="emp-bulk-import-trigger"><Upload className="mr-2 h-4 w-4" />Bulk import</Button></DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Bulk import employees</DialogTitle></DialogHeader>
            <div className="flex flex-col gap-3 py-2">
              <Button variant="outline" onClick={downloadTemplate} data-testid="emp-template-download"><Download className="mr-2 h-4 w-4" />Download Excel template</Button>
              <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] leading-relaxed text-amber-900" data-testid="emp-bulk-hint">
                <span className="font-semibold">Designation synonyms</span> &mdash;
                Contractor terms like <code className="px-1 rounded bg-white">Mazdoor</code>, <code className="px-1 rounded bg-white">Helper</code>,
                <code className="px-1 rounded bg-white">Cleaner</code>, <code className="px-1 rounded bg-white">Maistry</code> and <code className="px-1 rounded bg-white">Incharge</code> are
                automatically canonicalised to <strong>Sweeper</strong> / <strong>Driver</strong> / <strong>Supervisor</strong> on import.
              </div>
              <FieldLite label="Excel file (.xlsx) *"><Input type="file" accept=".xlsx" onChange={(e)=>setBulkExcel(e.target.files?.[0])} data-testid="emp-bulk-excel-input" /></FieldLite>
              <FieldLite label="Photos zip (optional)"><Input type="file" accept=".zip" onChange={(e)=>setBulkZip(e.target.files?.[0])} data-testid="emp-bulk-zip-input" /></FieldLite>
              {bulkResult && (
                <div className="rounded-lg border p-3">
                  <div className="text-sm">
                    Imported <b>{bulkResult.imported}</b>
                    {bulkResult.updated > 0 ? ` (inserted: ${bulkResult.inserted}, updated: ${bulkResult.updated})` : ""}
                    , skipped <b>{bulkResult.skipped}</b>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <details className="mt-2 text-xs"><summary className="cursor-pointer text-muted-foreground">{bulkResult.errors.length} errors</summary>
                      <ul className="mt-1 list-disc pl-5">{bulkResult.errors.slice(0, 30).map((er, i) => <li key={i}>{er}</li>)}</ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setBulkOpen(false)}>Close</Button>
              <Button onClick={submitBulk} disabled={bulkBusy} data-testid="emp-bulk-submit"><Upload className="mr-2 h-4 w-4" />{bulkBusy ? "Importing…" : "Import"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button onClick={openCreate} disabled={!parkId} data-testid="emp-add-button"><Plus className="mr-2 h-4 w-4" />Add employee</Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="emp-export-button"><Download className="mr-2 h-4 w-4" />Export Excel</Button>
      </div>
    }>
      {/* Dashboard KPI Summary Strip */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="emp-dashboard-kpis">
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-sky-200 bg-sky-50 text-sky-700">
            <TreePine className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Parks</div>
            <div className="font-mono text-xl font-bold tabular-nums text-slate-900" data-testid="kpi-total-parks">{parks.length}</div>
            <div className="text-[10px] text-slate-400">Industrial parks under PMC</div>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700">
            <UsersIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Headcount</div>
            <div className="font-mono text-xl font-bold tabular-nums text-slate-900" data-testid="kpi-total-headcount">{employees.length}</div>
            <div className="text-[10px] text-slate-400">Total registered workers</div>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Approved Roster</div>
            <div className="font-mono text-xl font-bold tabular-nums text-slate-900" data-testid="kpi-approved-headcount">{approvedCount}</div>
            <div className="text-[10px] text-slate-400">Regular headcount</div>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:shadow-md">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-purple-200 bg-purple-50 text-purple-700">
            <UserMinus className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Relievers</div>
            <div className="font-mono text-xl font-bold tabular-nums text-slate-900" data-testid="kpi-reliever-headcount">{relieverCount}</div>
            <div className="text-[10px] text-slate-400">Substitute staff</div>
          </div>
        </div>
      </div>

      <Card className="mb-4">
        <CardContent className="p-3 grid gap-2 md:grid-cols-3">
          <Select value={contractorId || "__all__"} onValueChange={(v)=>{ setContractorId(v === "__all__" ? "" : v); setParkId(""); }}>
            <SelectTrigger data-testid="emp-contractor-filter"><SelectValue placeholder="Choose contractor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All contractors</SelectItem>
              {contractors.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm">
                  <div className="font-medium text-foreground">No contractors yet</div>
                  <div className="mt-1 text-xs text-muted-foreground">Add one in Settings → Data Management → Contractors</div>
                  <Link to="/admin/contractors" className="mt-2 inline-block text-xs font-medium text-primary hover:underline">Go to Contractors →</Link>
                </div>
              ) : contractors.map((c)=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={parkId || "__all__"} onValueChange={(v)=>setParkId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="emp-park-filter"><SelectValue placeholder="Choose park" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="__all__">All parks</SelectItem>
              {parks.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm">
                  <div className="font-medium text-foreground">No parks yet</div>
                  <div className="mt-1 text-xs text-muted-foreground">Add one in Settings → Data Management → Parks</div>
                  <Link to="/admin/parks" className="mt-2 inline-block text-xs font-medium text-primary hover:underline">Go to Parks →</Link>
                </div>
              ) : [...parks].sort((a,b)=>String(a.code).localeCompare(String(b.code))).map((p)=><SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Search by name or code…" value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="emp-search-input" />
        </CardContent>
      </Card>

      {/* SUPERVISOR strip for selected (contractor, park) */}
      {parkId && (
        <Card className="mb-4">
          <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3 flex-1">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-secondary"><UserCog className="h-5 w-5" /></div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Contractor supervisor for <span className="font-mono font-semibold text-foreground">{parkObj?.code}</span>
                  {parkObj?.name ? <span className="ml-1 text-foreground/80 normal-case tracking-normal">— {parkObj.name}</span> : null}
                </div>
                {supState.loading ? <div className="text-sm text-muted-foreground">Loading…</div>
                  : supState.supervisor ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm font-medium truncate" data-testid="emp-current-supervisor">
                        {supState.supervisor.full_name} <span className="font-mono text-xs text-muted-foreground">@{supState.supervisor.username}</span>
                      </div>
                      {supState.supervisor.incharge_user_name ? (
                        <Badge variant="outline" className="border-sky-300 bg-sky-50 text-sky-800 text-[11px]" data-testid="emp-supervisor-incharge-badge">
                          In-charge: {supState.supervisor.incharge_user_name} (@{supState.supervisor.incharge_username})
                        </Badge>
                      ) : (
                        <span className="text-[11px] text-muted-foreground italic">No In-charge assigned</span>
                      )}
                    </div>
                  ) : <div className="text-sm text-muted-foreground">Not assigned</div>}
                <div className="text-[11px] text-muted-foreground">Contractor: {contractorObj?.name}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => openSupDialog("assign")} data-testid="emp-assign-existing-sup-button">Assign existing</Button>
              <Button size="sm" onClick={() => openSupDialog("create")} data-testid="emp-create-sup-button"><UserPlus2 className="mr-1.5 h-4 w-4" />Create supervisor</Button>
              <div className="flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50/50 px-2 py-1" data-testid="emp-incharge-select-wrapper">
                <UserCog className="h-4 w-4 text-sky-700 shrink-0" />
                <span className="text-xs font-semibold text-sky-900 whitespace-nowrap">In-Charge:</span>
                <Select
                  value={assignedIncharge.length > 0 ? assignedIncharge[0].id : "_none"}
                  onValueChange={async (val) => {
                    if (val === "_none") {
                      if (assignedIncharge.length > 0) {
                        await handleUnassignIncharge(assignedIncharge[0].id);
                      }
                    } else {
                      await handleAssignIncharge(val);
                    }
                  }}
                  disabled={loadingIncharge || !parkId}
                >
                  <SelectTrigger className="h-8 w-56 text-xs bg-white border-sky-300 focus:ring-sky-500" data-testid="emp-incharge-select">
                    <SelectValue placeholder={loadingIncharge ? "Loading…" : "Select Sweeper / Driver"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="_none">
                      <span className="text-slate-500 italic">No In-charge assigned</span>
                    </SelectItem>
                    {sweepersAndDriversOnly.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center justify-between w-full gap-2">
                          <span>{emp.full_name} ({emp.employee_code})</span>
                          <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal bg-sky-50 text-sky-800 border-sky-300">
                            {emp.designation || "Sweeper/Driver"}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {supState.supervisor && <Button variant="ghost" size="sm" onClick={unassignSup} className="text-red-600" data-testid="emp-unassign-sup-button">Remove</Button>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* APPROVED vs RELIEVER summary strip (per selected park) */}
      {parkId && summary && (
        <Card className="mb-4" data-testid="emp-approved-summary">
          <CardContent className="p-3">
            <div className="mb-2 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-700" />
              <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">SIPCOT approved roster · {parkObj?.name}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {summary.map((row) => (
                <div key={row.cat} className="rounded-md border border-amber-200 bg-amber-50/50 p-2" data-testid={`emp-bucket-${row.cat.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-900">{row.cat}</div>
                    <div className="font-mono text-[11px] text-amber-700">approved {row.approved}</div>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-mono text-2xl font-semibold tabular-nums text-amber-900">{row.actual_approved}</span>
                    <span className="text-[11px] text-amber-700/80">/ {row.approved} payable</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-1 text-[10px]">
                    {row.reliever > 0 && (
                      <span className="rounded-full bg-slate-200 text-slate-700 px-1.5 py-0.5 font-medium">{row.reliever} reliever{row.reliever > 1 ? "s" : ""}</span>
                    )}
                    {row.shortage > 0 && (
                      <span className="rounded-full bg-rose-100 text-rose-800 px-1.5 py-0.5 font-medium">short by {row.shortage}</span>
                    )}
                    {row.shortage === 0 && row.actual_approved >= row.approved && row.approved > 0 && (
                      <span className="rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5 font-medium">fully staffed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Tabs */}
      <div className="mb-3">
        <Tabs value={empCategoryTab} onValueChange={setEmpCategoryTab} className="w-full">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" data-testid="emp-tab-all">All Employees ({employees.length})</TabsTrigger>
            <TabsTrigger value="sweeper" data-testid="emp-tab-sweepers">Sweepers</TabsTrigger>
            <TabsTrigger value="driver" data-testid="emp-tab-drivers">Drivers</TabsTrigger>
            <TabsTrigger value="incharge" data-testid="emp-tab-incharge">
              <UserCog className="mr-1.5 h-3.5 w-3.5 text-sky-600" />
              In-Charge
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {empCategoryTab === "incharge" && (
        <Card className="mb-4 border-sky-200 bg-sky-50/20" data-testid="emp-incharge-management-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-sky-950">
              <UserCog className="h-5 w-5 text-sky-700" />
              Park-wise In-Charge Personnel Assignment
              {parkId && (
                <Badge variant="outline" className="ml-auto border-sky-300 bg-sky-100 text-sky-900 font-mono">
                  {parks.find(p => p.id === parkId)?.code} — {parks.find(p => p.id === parkId)?.name}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!parkId ? (
              <div className="rounded-lg border border-sky-200 bg-white p-6 text-center text-sm text-sky-900">
                <TreePine className="mx-auto mb-2 h-8 w-8 text-sky-600" />
                <div className="font-semibold">Select a Park</div>
                <div className="mt-1 text-xs text-slate-600">Please choose a park from the "Choose park" filter above to view and assign In-Charge personnel for that park.</div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {/* Currently Assigned In-Charge */}
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-700 flex items-center justify-between">
                    <span>Assigned In-Charge Personnel</span>
                    <Badge variant="secondary" className="font-mono text-[10px]">{assignedIncharge.length}</Badge>
                  </div>
                  {assignedIncharge.length === 0 ? (
                    <div className="py-6 text-center text-xs text-slate-500 italic">
                      No In-Charge employee assigned yet for this park. Select a candidate below to assign.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {assignedIncharge.map((emp) => (
                        <div key={emp.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 p-2.5">
                          <div>
                            <div className="font-medium text-sm text-slate-900">{emp.full_name}</div>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span className="font-mono">{emp.employee_code}</span>
                              <span>·</span>
                              <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal bg-sky-50 text-sky-800 border-sky-300">
                                {emp.designation || "Sweeper/Driver"}
                              </Badge>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50"
                            onClick={() => handleUnassignIncharge(emp.id)}
                            data-testid={`emp-unassign-incharge-${emp.employee_code}`}
                          >
                            <UserMinus className="mr-1 h-3.5 w-3.5" />
                            Unassign
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Sweepers & Drivers Candidates */}
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-700 flex items-center justify-between">
                    <span>Available Sweepers & Drivers</span>
                    <Badge variant="secondary" className="font-mono text-[10px]">{sweepersAndDriversOnly.length}</Badge>
                  </div>
                  {sweepersAndDriversOnly.length === 0 ? (
                    <div className="py-6 text-center text-xs text-slate-500 italic">
                      No available Sweepers or Drivers registered for this park.
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                      {sweepersAndDriversOnly.map((cand) => {
                        const isAlreadyAssigned = assignedIncharge.some(a => a.id === cand.id);
                        return (
                          <div key={cand.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 p-2.5">
                            <div>
                              <div className="font-medium text-sm text-slate-900">{cand.full_name}</div>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span className="font-mono">{cand.employee_code}</span>
                                <span>·</span>
                                <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal">
                                  {cand.designation}
                                </Badge>
                              </div>
                            </div>
                            {isAlreadyAssigned ? (
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-[10px]">
                                Assigned
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 border-sky-300 text-sky-800 hover:bg-sky-50"
                                onClick={() => handleAssignIncharge(cand.id)}
                                data-testid={`emp-assign-incharge-${cand.employee_code}`}
                              >
                                <UserCog className="mr-1 h-3.5 w-3.5" />
                                Assign In-Charge
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* EMPLOYEE list (table) */}
      {loadingEmps ? <div className="text-sm text-muted-foreground">Loading…</div>
        : filtered.length === 0 ? <Card><CardContent className="p-8 text-center text-sm text-muted-foreground"><IdCard className="mx-auto mb-2 h-8 w-8" />No employees match the current filter.</CardContent></Card>
        : <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="emp-list">
              <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                <tr className="border-b border-slate-200">
                  <Th>#</Th>
                  <Th>Code</Th>
                  <Th className="text-left">Name</Th>
                  <Th>Park Code</Th>
                  <Th className="text-left">Park Name</Th>
                  <Th>Category</Th>
                  <Th>Type</Th>
                  <Th>Face</Th>
                  <Th>ID Proof</Th>
                  <Th>Bank</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, idx) => {
                  const rowCls = e.is_reliever
                    ? "bg-slate-100 border-l-2 border-l-slate-400 hover:bg-slate-200/80 cursor-pointer"
                    : "hover:bg-amber-50/50 cursor-pointer";
                  const parkOfRow = parks.find(p => p.id === e.park_id);
                  return (
                    <tr
                      key={e.id}
                      className={`border-b border-slate-100 ${rowCls}`}
                      data-testid={`emp-row-${e.employee_code}`}
                      onClick={() => openView(e)}
                    >
                      <Td><span className="font-mono text-xs text-slate-500 tabular-nums">{idx + 1}</span></Td>
                      <Td><span className="font-mono text-xs font-semibold text-slate-800">{e.employee_code}</span></Td>
                      <Td className="text-left">
                        <span className="font-medium text-slate-900">{e.full_name}</span>
                      </Td>
                      <Td>
                        {parkOfRow ? <span className="font-mono text-xs font-semibold text-slate-800">{parkOfRow.code}</span> : <span className="text-slate-400">—</span>}
                      </Td>
                      <Td className="text-left">
                        {parkOfRow ? <span className="text-xs text-slate-700">{parkOfRow.name}</span> : <span className="text-slate-400">—</span>}
                      </Td>
                      <Td><span className="text-xs text-slate-700">{e.designation || "—"}</span></Td>
                      <Td>
                        {e.is_reliever ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-800" data-testid={`emp-badge-reliever-${e.employee_code}`}>
                            <UserMinus className="h-3 w-3" />Reliever
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900" data-testid={`emp-badge-approved-${e.employee_code}`}>
                            <ShieldCheck className="h-3 w-3" />Approved
                          </span>
                        )}
                      </Td>
                      <Td>
                        {e.has_face_encoding
                          ? <span className="rounded-full bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900" title="Face vector generated">Ready</span>
                          : (e.has_photo || e.reference_photo_b64)
                          ? <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900" title="Photo uploaded; face encoding pending">Photo saved</span>
                          : <span className="rounded-full bg-rose-200 px-1.5 py-0.5 text-[10px] font-semibold text-rose-900">No photo</span>}
                      </Td>
                      <Td>{e.has_id_card_attachment
                          ? <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900" title="ID proof attached">✓</span>
                          : <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" title="No ID proof">—</span>}
                      </Td>
                      <Td>{e.has_bank_passbook_attachment
                          ? <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900" title="Passbook attached">✓</span>
                          : <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" title="No passbook">—</span>}
                      </Td>
                      <Td>
                        <div className="flex justify-center gap-1" onClick={(ev)=>ev.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="View details" onClick={() => openView(e)} data-testid={`emp-view-${e.employee_code}`}><Eye className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => openEdit(e)} data-testid={`emp-edit-${e.employee_code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Toggle reliever" onClick={() => toggleReliever(e)} data-testid={`emp-toggle-reliever-${e.employee_code}`}>
                            {e.is_reliever ? <ShieldCheck className="h-3.5 w-3.5 text-amber-700" /> : <UserMinus className="h-3.5 w-3.5 text-slate-700" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50" title="Delete" onClick={() => remove(e)} data-testid={`emp-delete-${e.employee_code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-amber-200 ring-1 ring-amber-400" />Approved (payable)</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-slate-200 ring-1 ring-slate-400" />Reliever (substitute only)</span>
            <span className="ml-auto text-slate-500">{filtered.length} of {employees.length} workers shown</span>
          </div>
        </Card>
      }

      {/* EMPLOYEE create/edit dialog */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit employee" : "New employee"}</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Contractor: <span className="font-medium text-foreground">{contractorObj?.name}</span> · Park: <span className="font-medium text-foreground">{parkObj?.name}</span>
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <FieldLite label="Employee code *"><Input disabled={!!editing} value={form.employee_code} onChange={(e)=>setForm({...form,employee_code:e.target.value})} data-testid="emp-code-input" /></FieldLite>
            <FieldLite label="Full name *"><Input value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})} data-testid="emp-name-input" /></FieldLite>
            <FieldLite label="Category *">
              <Select value={form.designation} onValueChange={(v)=>setForm({...form,designation:v})}>
                <SelectTrigger data-testid="emp-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>{categories.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </FieldLite>
            <FieldLite label="Phone"><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} /></FieldLite>
            <FieldLite label="Reference photo (face will be encoded)" full>
              <div className="flex items-center gap-3">
                {form.reference_photo_b64
                  ? <img alt="ref" src={form.reference_photo_b64} className="h-20 w-20 rounded-lg object-cover border" />
                  : <div className="h-20 w-20 rounded-lg border grid place-items-center bg-muted"><ScanFace className="h-6 w-6 text-muted-foreground" /></div>
                }
                <Input type="file" accept="image/*" onChange={(e)=>onPhoto(e.target.files?.[0])} data-testid="emp-photo-input" />
              </div>
            </FieldLite>

            {/* IDENTITY PROOF */}
            <div className="col-span-2 mt-2 rounded-md border border-amber-300 bg-amber-50/60 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 mb-2">Identity proof</div>
              <div className="grid grid-cols-2 gap-3">
                <FieldLite label="ID card type">
                  <Select value={form.id_card_type} onValueChange={(v)=>setForm({...form, id_card_type: v})}>
                    <SelectTrigger data-testid="emp-id-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aadhar">Aadhar card</SelectItem>
                      <SelectItem value="voter_id">Voter ID</SelectItem>
                      <SelectItem value="bank_passbook">Bank Passbook</SelectItem>
                      <SelectItem value="ration_card">Ration card</SelectItem>
                      <SelectItem value="pan_card">PAN card</SelectItem>
                      <SelectItem value="other">Other (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldLite>
                {form.id_card_type === "other" && (
                  <FieldLite label="Specify type">
                    <Input value={form.id_card_other_label} onChange={(e)=>setForm({...form, id_card_other_label: e.target.value})} placeholder="e.g. Driving licence" data-testid="emp-id-other-label" />
                  </FieldLite>
                )}
                <FieldLite label="ID number" full={form.id_card_type !== "other"}>
                  <Input value={form.id_card_number} onChange={(e)=>setForm({...form, id_card_number: e.target.value})} data-testid="emp-id-number" />
                </FieldLite>
                <FieldLite label="ID attachment (image / PDF)" full>
                  <div className="flex items-center gap-3">
                    <Input type="file" accept="image/*,application/pdf" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const b64 = await fileToBase64(f);
                      setForm((p)=>({ ...p, id_card_attachment_b64: b64 }));
                    }} data-testid="emp-id-attachment" />
                    {form.id_card_attachment_b64 && <span className="text-[11px] text-emerald-700 font-medium">✓ ready</span>}
                    {editing?.has_id_card_attachment && !form.id_card_attachment_b64 && <span className="text-[11px] text-emerald-700">existing attachment on file</span>}
                  </div>
                </FieldLite>
              </div>
            </div>

            {/* BANK ACCOUNT */}
            <div className="col-span-2 mt-1 rounded-md border border-emerald-300 bg-emerald-50/60 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-900 mb-2">Bank account</div>
              <div className="grid grid-cols-2 gap-3">
                <FieldLite label="Account number">
                  <Input value={form.bank_account_number} onChange={(e)=>setForm({...form, bank_account_number: e.target.value})} data-testid="emp-bank-acct" />
                </FieldLite>
                <FieldLite label="Bank name">
                  <Input value={form.bank_name} onChange={(e)=>setForm({...form, bank_name: e.target.value})} data-testid="emp-bank-name" />
                </FieldLite>
                <FieldLite label="Branch">
                  <Input value={form.bank_branch} onChange={(e)=>setForm({...form, bank_branch: e.target.value})} data-testid="emp-bank-branch" />
                </FieldLite>
                <FieldLite label="IFSC code">
                  <Input value={form.bank_ifsc} onChange={(e)=>setForm({...form, bank_ifsc: e.target.value.toUpperCase()})} data-testid="emp-bank-ifsc" />
                </FieldLite>
                <FieldLite label="Passbook front page (image / PDF)" full>
                  <div className="flex items-center gap-3">
                    <Input type="file" accept="image/*,application/pdf" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const b64 = await fileToBase64(f);
                      setForm((p)=>({ ...p, bank_passbook_attachment_b64: b64 }));
                    }} data-testid="emp-bank-attachment" />
                    {form.bank_passbook_attachment_b64 && <span className="text-[11px] text-emerald-700 font-medium">✓ ready</span>}
                    {editing?.has_bank_passbook_attachment && !form.bank_passbook_attachment_b64 && <span className="text-[11px] text-emerald-700">existing attachment on file</span>}
                  </div>
                </FieldLite>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEmpOpen(false)}>Cancel</Button>
            <Button onClick={submitEmp} disabled={saving} data-testid="emp-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SUPERVISOR assignment dialog */}
      <Dialog open={supDialogOpen} onOpenChange={setSupDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{supMode === "create" ? "Create contractor supervisor" : "Assign existing supervisor"}</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">{contractorObj?.name} · {parkObj?.name}</div>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            {supMode === "create" ? <>
              <div className="grid grid-cols-2 gap-3">
                <FieldLite label="Username *"><Input value={supForm.username} onChange={(e)=>setSupForm({...supForm,username:e.target.value})} data-testid="emp-sup-username-input" /></FieldLite>
                <FieldLite label="Full name *"><Input value={supForm.full_name} onChange={(e)=>setSupForm({...supForm,full_name:e.target.value})} data-testid="emp-sup-fullname-input" /></FieldLite>
                <FieldLite label="Password *" full><PasswordInput value={supForm.password} onChange={(e)=>setSupForm({...supForm,password:e.target.value})} data-testid="emp-sup-password-input" /></FieldLite>
                <FieldLite label="Phone"><Input value={supForm.phone} onChange={(e)=>setSupForm({...supForm,phone:e.target.value})} /></FieldLite>
                <FieldLite label="Email"><Input value={supForm.email} onChange={(e)=>setSupForm({...supForm,email:e.target.value})} /></FieldLite>
              </div>
            </> : <>
              {allContractorSups.length === 0 ? <div className="text-sm text-muted-foreground">No reusable supervisors. Each contractor supervisor is bound to one park &mdash; use &ldquo;Create supervisor&rdquo; to make a fresh login for {parkObj?.name}.</div>
                : <Select value={assignSelectId} onValueChange={setAssignSelectId}>
                  <SelectTrigger data-testid="emp-sup-assign-select"><SelectValue placeholder="Pick a supervisor" /></SelectTrigger>
                  <SelectContent>{allContractorSups.map((u)=><SelectItem key={u.id} value={u.id}>{u.full_name} (@{u.username}) — {(u.park_ids||[]).length} park(s)</SelectItem>)}</SelectContent>
                </Select>
              }
            </>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSupDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitSup} disabled={savingSup} data-testid="emp-sup-save-button">{savingSup ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EMPLOYEE detail / view dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="emp-view-dialog">
          {viewing && (() => {
            const parkOfRow = parks.find(p => p.id === viewing.park_id);
            const downloadAttachment = async (kind) => {
              try {
                const tk = tokenStore.get();
                const res = await fetch(`${API_BASE}/employees/${viewing.id}/attachment/${kind}`, { headers: { Authorization: `Bearer ${tk}` } });
                if (!res.ok) return toast.error("Attachment not available");
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = res.headers.get("Content-Disposition")?.match(/filename="?(.+?)"?$/)?.[1] || `${viewing.employee_code}_${kind}`;
                a.click();
                URL.revokeObjectURL(a.href);
              } catch { toast.error("Download failed"); }
            };
            const ID_LABEL = { aadhar: "Aadhar card", voter_id: "Voter ID", bank_passbook: "Bank Passbook", ration_card: "Ration card", pan_card: "PAN card", other: viewing.id_card_other_label || "Other" };
            return <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{viewing.full_name}
                  {viewing.is_reliever
                    ? <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-800">Reliever</span>
                    : <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-900">Approved</span>}
                </DialogTitle>
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="font-mono mr-2">{viewing.employee_code}</span> · {viewing.designation || "—"} · {parkOfRow ? `${parkOfRow.code} ${parkOfRow.name}` : "—"}
                </div>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 py-2 text-sm">
                <Stat l="Phone" v={viewing.phone || "—"} />
                <Stat l="Active" v={viewing.active ? "Yes" : "No"} />

                <div className="col-span-2 mt-1 rounded-md border border-amber-300 bg-amber-50/60 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-900">Identity proof</div>
                    {viewing.has_id_card_attachment && (
                      <Button size="sm" variant="outline" className="h-7" onClick={() => downloadAttachment("id_card")} data-testid="emp-view-download-id">
                        <FileDown className="mr-1.5 h-3.5 w-3.5" />Download
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Stat l="Type" v={ID_LABEL[viewing.id_card_type] || "—"} />
                    <Stat l="Number" v={viewing.id_card_number || "—"} mono />
                    <Stat l="Attachment" v={viewing.has_id_card_attachment ? "✓ On file" : "Not uploaded"} accent={viewing.has_id_card_attachment ? "emerald" : "slate"} />
                  </div>
                </div>

                <div className="col-span-2 rounded-md border border-emerald-300 bg-emerald-50/60 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-900">Bank account</div>
                    {viewing.has_bank_passbook_attachment && (
                      <Button size="sm" variant="outline" className="h-7" onClick={() => downloadAttachment("bank_passbook")} data-testid="emp-view-download-bank">
                        <FileDown className="mr-1.5 h-3.5 w-3.5" />Download passbook
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Stat l="Account no." v={viewing.bank_account_number || "—"} mono />
                    <Stat l="Bank" v={viewing.bank_name || "—"} />
                    <Stat l="Branch" v={viewing.bank_branch || "—"} />
                    <Stat l="IFSC" v={viewing.bank_ifsc || "—"} mono />
                    <Stat l="Passbook" v={viewing.has_bank_passbook_attachment ? "✓ On file" : "Not uploaded"} accent={viewing.has_bank_passbook_attachment ? "emerald" : "slate"} />
                  </div>
                </div>

                <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50/80 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 mb-2">Reference photo & face status</div>
                  <div className="flex items-center gap-4">
                    {viewing.photo_b64 ? (
                      <img alt="Reference" src={viewing.photo_b64} className="h-24 w-24 rounded-lg object-cover border border-slate-300 shadow-sm" />
                    ) : (viewing.has_photo || viewing.reference_photo_b64) ? (
                      <div className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border border-amber-300 bg-amber-50 p-2 text-center text-xs text-amber-800">
                        <ScanFace className="h-6 w-6 text-amber-600 mb-1" />
                        <span>Photo saved</span>
                      </div>
                    ) : (
                      <div className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-100 p-2 text-center text-xs text-slate-400">
                        <span>No photo</span>
                      </div>
                    )}
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-slate-900">
                        {(viewing.has_photo || viewing.photo_b64) ? "Reference Photo Uploaded" : "No Photo Uploaded"}
                      </div>
                      <div className="text-[11px] text-slate-500 leading-normal max-w-sm">
                        {viewing.has_face_encoding
                          ? "Face encoding generated cleanly for automated attendance face-matching."
                          : (viewing.has_photo || viewing.photo_b64)
                          ? "Photo saved on file. (Face encoding pending or document/paper image uploaded)."
                          : "Upload a face photo or passport selfie to enable automated attendance face-matching."}
                      </div>
                      <div className="pt-1">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          viewing.has_face_encoding
                            ? "bg-emerald-200 text-emerald-900"
                            : (viewing.has_photo || viewing.photo_b64)
                            ? "bg-amber-200 text-amber-900"
                            : "bg-rose-200 text-rose-900"
                        }`}>
                          {viewing.has_face_encoding ? "Ready (Face Encoded)" : (viewing.has_photo || viewing.photo_b64) ? "Photo Saved" : "Awaiting Photo"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
                <Button onClick={() => { setViewOpen(false); openEdit(viewing); }} data-testid="emp-view-edit-button"><Pencil className="mr-1.5 h-4 w-4" />Edit</Button>
              </DialogFooter>
            </>;
          })()}
        </DialogContent>
      </Dialog>

    </AppShell>
  );
}

function FieldLite({ label, children, full }) {
  return (
    <div className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}


function Th({ children, className = "" }) {
  return <th className={`whitespace-nowrap px-3 py-2 text-center text-[11px] font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`whitespace-nowrap px-3 py-2 text-center align-middle ${className}`}>{children}</td>;
}

function Stat({ l, v, accent, mono }) {
  const cls = accent === "emerald" ? "text-emerald-800"
    : accent === "rose" ? "text-rose-800"
    : accent === "slate" ? "text-slate-600"
    : "text-slate-900";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{l}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} ${cls}`}>{v}</span>
    </div>
  );
}

